// Extracted from BatucadaRio.jsx — InstrumentOptionsTab

import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Tone from 'tone';
import {
  Play, Pause, Square, Volume2, Search, Download, Settings, ArrowLeft,
  Drum, Clock, SkipBack, Repeat, Layers, ChevronLeft, ChevronRight,
  Sliders, Grid3X3, FileAudio, Code, Package, FolderUp, Save, ChevronUp, ChevronDown,
  Trash2, X
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================
// INSTRUMENT OPTIONS TAB (Functional)
// ============================================
export const InstrumentOptionsTab = ({ sample, selectedKey, settings, onSettingsChange }) => {
  const [volume, setVolume] = useState(settings?.volume ?? 80);
  const [pan, setPan] = useState(settings?.pan ?? 0);
  const [tune, setTune] = useState(settings?.tune ?? 0);
  const [attack, setAttack] = useState(settings?.attack ?? 0);
  const [decay, setDecay] = useState(settings?.decay ?? 0);
  const [sustain, setSustain] = useState(settings?.sustain ?? 100);
  const [release, setRelease] = useState(settings?.release ?? 10);

  // Update local state when settings change
  useEffect(() => {
    if (settings) {
      setVolume(settings.volume ?? 80);
      setPan(settings.pan ?? 0);
      setTune(settings.tune ?? 0);
      setAttack(settings.attack ?? 0);
      setDecay(settings.decay ?? 0);
      setSustain(settings.sustain ?? 100);
      setRelease(settings.release ?? 10);
    }
  }, [settings, selectedKey]);

  const handleApply = () => {
    onSettingsChange?.(selectedKey, {
      ...settings,
      volume,
      pan,
      tune,
      attack,
      decay,
      sustain,
      release
    });
  };

  return (
    <div className="space-y-4">
      {/* Main Controls Row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#222] rounded-lg p-4">
          <h4 className="text-xs text-gray-400 uppercase tracking-wide mb-3">Volume</h4>
          <div className="flex items-center gap-3">
            <Volume2 className="w-4 h-4 text-gray-500" />
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              className="flex-1 accent-amber-500" 
            />
            <span className="text-xs text-white w-10">{volume}%</span>
          </div>
        </div>
        <div className="bg-[#222] rounded-lg p-4">
          <h4 className="text-xs text-gray-400 uppercase tracking-wide mb-3">Pan</h4>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-gray-500">L</span>
            <input 
              type="range" 
              min="-100" 
              max="100" 
              value={pan}
              onChange={(e) => setPan(Number(e.target.value))}
              className="flex-1 accent-amber-500" 
            />
            <span className="text-[10px] text-gray-500">R</span>
            <span className="text-xs text-white w-10">{pan > 0 ? `R${pan}` : pan < 0 ? `L${Math.abs(pan)}` : 'C'}</span>
          </div>
        </div>
        <div className="bg-[#222] rounded-lg p-4">
          <h4 className="text-xs text-gray-400 uppercase tracking-wide mb-3">Tune</h4>
          <div className="flex items-center gap-3">
            <input 
              type="range" 
              min="-24" 
              max="24" 
              value={tune}
              onChange={(e) => setTune(Number(e.target.value))}
              className="flex-1 accent-amber-500" 
            />
            <span className="text-xs text-white w-12">{tune > 0 ? `+${tune}` : tune} st</span>
          </div>
        </div>
      </div>

      {/* ADSR Envelope */}
      <div className="bg-[#222] rounded-lg p-4">
        <h4 className="text-xs text-gray-400 uppercase tracking-wide mb-3">Envelope (ADSR)</h4>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="text-[10px] text-gray-500 block mb-1">Attack</label>
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={attack}
              onChange={(e) => setAttack(Number(e.target.value))}
              className="w-full accent-green-500" 
            />
            <span className="text-[10px] text-white">{attack}ms</span>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 block mb-1">Decay</label>
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={decay}
              onChange={(e) => setDecay(Number(e.target.value))}
              className="w-full accent-yellow-500" 
            />
            <span className="text-[10px] text-white">{decay}ms</span>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 block mb-1">Sustain</label>
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={sustain}
              onChange={(e) => setSustain(Number(e.target.value))}
              className="w-full accent-orange-500" 
            />
            <span className="text-[10px] text-white">{sustain}%</span>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 block mb-1">Release</label>
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={release}
              onChange={(e) => setRelease(Number(e.target.value))}
              className="w-full accent-red-500" 
            />
            <span className="text-[10px] text-white">{release}ms</span>
          </div>
        </div>
      </div>

      {/* Sample Info & Apply */}
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-gray-500">
          Settings for: <span className="text-amber-400">{sample?.name || 'None'}</span> ({selectedKey})
        </p>
        <button
          onClick={handleApply}
          className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-black text-xs font-medium rounded transition-colors"
        >
          Apply Settings
        </button>
      </div>
    </div>
  );
};
